/**
 * Contract Options Admin — page dédiée pour gérer les rôles et régions
 * disponibles dans la génération de contrats.
 *
 * Deux tableaux CRUD :
 *   - **Rôles de contrat** : chaque rôle a un `template_base` (RD ou RE)
 *     qui décide de la branche du template HTML utilisée au render.
 *   - **Régions** : simple label qui apparaît dans l'article 4 du contrat.
 *
 * Les entrées "built-in" (livrées avec l'app) sont marquées d'un badge
 * INTÉGRÉ et non supprimables. Elles peuvent cependant être **dupliquées**
 * en un clic pour créer une variante custom (ex : "Recruteur de donateurs"
 * → "Recruteur de donateurs premium").
 */
import { motion } from "framer-motion";
import {
    Briefcase,
    Copy,
    Loader2,
    MapPin,
    Plus,
    ShieldCheck,
    Trash2,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { AddContractOptionDialog } from "../components/AddContractOptionDialog";
import { Button } from "../components/ui/button";
import { useContractOptions } from "../hooks/useContractOptions";


// The `template_base` value → the label admin sees in the table.
const TEMPLATE_BASE_LABEL = {
    recruteur_donateurs: "Type Recruteur (taux horaire)",
    responsable_equipe: "Type Responsable (salaire mensuel)",
};


function StatusBadge({ editable }) {
    if (editable) {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest bg-shariti-orange-soft text-shariti-orange rounded-md">
                Personnalisé
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest bg-slate-100 text-slate-600 rounded-md">
            <ShieldCheck className="h-3 w-3" /> Intégré
        </span>
    );
}


function RolesTable({ roles, createRole, deleteRole }) {
    const [duplicatingKey, setDuplicatingKey] = useState(null);

    async function duplicate(src) {
        setDuplicatingKey(src.key);
        try {
            const base = src.label_long || src.label;
            // Naive de-duplication: append " (copie)" and let the backend
            // slug it. If someone dupes twice we'll get a 409 they can retry
            // with a manual label change via the "+" flow.
            const nextLabel = `${base} (copie)`;
            await createRole({
                label: nextLabel,
                template_base: src.template_base || "recruteur_donateurs",
            });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Impossible de dupliquer ce rôle");
        } finally {
            setDuplicatingKey(null);
        }
    }

    async function remove(role) {
        if (!window.confirm(`Supprimer le rôle « ${role.label} » ?`)) return;
        try {
            await deleteRole(role.key);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Impossible de supprimer ce rôle");
        }
    }

    return (
        <div className="card-soft p-6 sm:p-7" data-testid="roles-table-card">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
                <div className="flex items-center gap-2.5">
                    <div className="h-9 w-9 rounded-xl bg-shariti-orange-soft text-shariti-orange flex items-center justify-center">
                        <Briefcase className="h-4.5 w-4.5" />
                    </div>
                    <div>
                        <h2 className="text-base font-extrabold text-shariti-black tracking-tight">
                            Rôles de contrat
                        </h2>
                        <p className="text-[11px] text-slate-500 leading-snug mt-0.5">
                            Chaque rôle est associé à un modèle de contrat (Recruteur ou Responsable)
                            qui décide de la structure du PDF généré.
                        </p>
                    </div>
                </div>
                <AddContractOptionDialog
                    kind="role"
                    createRole={createRole}
                    createRegion={() => {}}
                    onCreated={() => toast.info("Le rôle est prêt à être utilisé dans un contrat.")}
                />
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="roles-table">
                    <thead className="text-[11px] font-black uppercase tracking-widest text-slate-500 border-b border-slate-200">
                        <tr>
                            <th className="text-left py-2 pr-4">Nom</th>
                            <th className="text-left py-2 pr-4">Modèle utilisé</th>
                            <th className="text-left py-2 pr-4">Statut</th>
                            <th className="text-right py-2 pl-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {roles.map((r) => (
                            <tr
                                key={r.key}
                                className="border-b border-slate-100 last:border-0 hover:bg-shariti-cream/40 transition"
                                data-testid={`role-row-${r.key}`}
                            >
                                <td className="py-3 pr-4">
                                    <div className="font-extrabold text-shariti-black">{r.label}</div>
                                    {r.label_long && r.label_long !== r.label && (
                                        <div className="text-[11px] text-slate-500 mt-0.5">
                                            Titre long : {r.label_long}
                                        </div>
                                    )}
                                    <div className="text-[10px] text-slate-400 font-mono mt-0.5">{r.key}</div>
                                </td>
                                <td className="py-3 pr-4 text-xs text-slate-700">
                                    {TEMPLATE_BASE_LABEL[r.template_base] || r.template_base || "—"}
                                </td>
                                <td className="py-3 pr-4">
                                    <StatusBadge editable={r.editable} />
                                </td>
                                <td className="py-3 pl-4 text-right">
                                    <div className="flex items-center justify-end gap-2">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => duplicate(r)}
                                            disabled={duplicatingKey === r.key}
                                            title="Dupliquer ce rôle"
                                            data-testid={`duplicate-role-${r.key}`}
                                            className="text-slate-600 hover:text-shariti-orange"
                                        >
                                            {duplicatingKey === r.key ? (
                                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                            ) : (
                                                <Copy className="h-3.5 w-3.5" />
                                            )}
                                            <span className="ml-1 text-[11px] font-bold hidden sm:inline">Dupliquer</span>
                                        </Button>
                                        {r.editable && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => remove(r)}
                                                title="Supprimer ce rôle"
                                                data-testid={`delete-role-${r.key}`}
                                                className="text-red-600 hover:bg-red-50"
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </Button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


function RegionsTable({ regions, createRegion, deleteRegion }) {
    async function remove(reg) {
        if (!window.confirm(`Supprimer la région « ${reg.label} » ?`)) return;
        try {
            await deleteRegion(reg.key);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Impossible de supprimer cette région");
        }
    }

    return (
        <div className="card-soft p-6 sm:p-7" data-testid="regions-table-card">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
                <div className="flex items-center gap-2.5">
                    <div className="h-9 w-9 rounded-xl bg-shariti-orange-soft text-shariti-orange flex items-center justify-center">
                        <MapPin className="h-4.5 w-4.5" />
                    </div>
                    <div>
                        <h2 className="text-base font-extrabold text-shariti-black tracking-tight">
                            Régions
                        </h2>
                        <p className="text-[11px] text-slate-500 leading-snug mt-0.5">
                            La région apparaît dans l&apos;article 4 du contrat (« principalement dans la région … »).
                        </p>
                    </div>
                </div>
                <AddContractOptionDialog
                    kind="region"
                    createRole={() => {}}
                    createRegion={createRegion}
                    onCreated={() => toast.info("La région est prête à être utilisée.")}
                />
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="regions-table">
                    <thead className="text-[11px] font-black uppercase tracking-widest text-slate-500 border-b border-slate-200">
                        <tr>
                            <th className="text-left py-2 pr-4">Nom</th>
                            <th className="text-left py-2 pr-4">Statut</th>
                            <th className="text-right py-2 pl-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {regions.map((r) => (
                            <tr
                                key={r.key}
                                className="border-b border-slate-100 last:border-0 hover:bg-shariti-cream/40 transition"
                                data-testid={`region-row-${r.key}`}
                            >
                                <td className="py-3 pr-4">
                                    <div className="font-extrabold text-shariti-black">{r.label}</div>
                                    <div className="text-[10px] text-slate-400 font-mono mt-0.5">{r.key}</div>
                                </td>
                                <td className="py-3 pr-4">
                                    <StatusBadge editable={r.editable} />
                                </td>
                                <td className="py-3 pl-4 text-right">
                                    {r.editable && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => remove(r)}
                                            title="Supprimer cette région"
                                            data-testid={`delete-region-${r.key}`}
                                            className="text-red-600 hover:bg-red-50"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


export default function ContractOptionsAdmin() {
    const { loading, roles, regions, createRole, createRegion, deleteRole, deleteRegion } = useContractOptions();

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24">
                <Loader2 className="h-6 w-6 animate-spin text-shariti-orange" />
            </div>
        );
    }

    const customRoleCount = roles.filter((r) => r.editable).length;
    const customRegionCount = regions.filter((r) => r.editable).length;

    return (
        <div className="max-w-5xl">
            {/* Header */}
            <div className="mb-8">
                <div className="text-[11px] font-extrabold uppercase tracking-[0.25em] text-shariti-orange mb-2 flex items-center gap-2" data-testid="contract-options-eyebrow">
                    <Plus className="h-3.5 w-3.5" />
                    Back-office · Paramètres
                </div>
                <h1 className="text-3xl sm:text-4xl font-black text-shariti-black font-display leading-tight mb-2" data-testid="contract-options-title">
                    Rôles &amp; régions de contrat
                </h1>
                <p className="text-sm text-slate-500 max-w-2xl leading-relaxed">
                    Gérez les rôles (postes) et les régions qui apparaissent dans les listes déroulantes
                    du formulaire de contrat. Vous pouvez ajouter des rôles personnalisés
                    (ex : <em>Recruteur expérimenté</em>) ou dupliquer un rôle existant pour en créer une variante.
                </p>
                <div className="mt-4 flex flex-wrap gap-2 text-[11px] font-bold" data-testid="contract-options-counters">
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-shariti-cream text-slate-600">
                        {roles.length} rôle{roles.length > 1 ? "s" : ""} au total
                        {customRoleCount > 0 && (
                            <span className="text-shariti-orange">
                                &nbsp;· {customRoleCount} personnalisé{customRoleCount > 1 ? "s" : ""}
                            </span>
                        )}
                    </span>
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-shariti-cream text-slate-600">
                        {regions.length} région{regions.length > 1 ? "s" : ""} au total
                        {customRegionCount > 0 && (
                            <span className="text-shariti-orange">
                                &nbsp;· {customRegionCount} personnalisée{customRegionCount > 1 ? "s" : ""}
                            </span>
                        )}
                    </span>
                </div>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
            >
                <RolesTable roles={roles} createRole={createRole} deleteRole={deleteRole} />
                <RegionsTable regions={regions} createRegion={createRegion} deleteRegion={deleteRegion} />
            </motion.div>
        </div>
    );
}
